import React, { useEffect, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";

const PROVIDER_LABELS = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
};

// Value used in the per-tenant dropdown for "no override". Sent to the API as
// an empty provider, which clears the column and returns the tenant to the
// platform default.
const INHERIT = "";

// Which chat channel notifications go out on. One platform default, overridable
// per tenant. Tenants left on "Default" follow the platform setting rather than
// being stamped with a copy of it, so changing the default moves all of them.
export default function MessagingPanel({ onError }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingDefault, setSavingDefault] = useState(false);
  const [savingTenantId, setSavingTenantId] = useState(null);
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const resp = await superadminAPI.getMessagingSettings();
      setSettings(resp.data);
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load messaging settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDefaultChange = async (provider) => {
    setSavingDefault(true);
    onError("");
    setNotice("");
    try {
      const resp = await superadminAPI.setPlatformMessagingProvider(provider);
      setSettings(resp.data);
      setNotice(`Platform default is now ${PROVIDER_LABELS[provider]}.`);
    } catch (err) {
      onError(err.response?.data?.message || "Failed to save platform default");
    } finally {
      setSavingDefault(false);
    }
  };

  const handleTenantChange = async (tenantId, provider) => {
    setSavingTenantId(tenantId);
    onError("");
    setNotice("");
    try {
      await superadminAPI.setTenantMessagingProvider(tenantId, provider);
      setSettings((prev) => ({
        ...prev,
        tenants: prev.tenants.map((t) =>
          t.id === tenantId ? { ...t, messagingProvider: provider || null } : t,
        ),
      }));
    } catch (err) {
      onError(err.response?.data?.message || "Failed to save tenant channel");
    } finally {
      setSavingTenantId(null);
    }
  };

  if (loading) {
    return <div className="superadmin-loading">Loading messaging settings…</div>;
  }
  if (!settings) return null;

  const { platformDefault, providers, envDefault, tenants } = settings;

  return (
    <div className="superadmin-panel">
      <div className="superadmin-panel-head">
        <h2>Messaging</h2>
        <p>
          The chat channel notifications are sent on. Workers reply on the same
          channel, so switching a tenant only takes effect for people who have
          the matching contact detail saved on their user record — a Telegram
          chat ID, or a WhatsApp number in <code>+91…</code> form.
        </p>
      </div>

      <div className="superadmin-messaging-default">
        <h3>Platform default</h3>
        <p>
          Applies to every tenant that has not chosen its own channel. Changing
          it moves all of them at once.
        </p>
        <div className="superadmin-messaging-choices">
          {providers.map((provider) => (
            <label key={provider}>
              <input
                type="radio"
                name="platform-messaging-provider"
                value={provider}
                checked={platformDefault === provider}
                disabled={savingDefault}
                onChange={() => handleDefaultChange(provider)}
              />
              {PROVIDER_LABELS[provider]}
            </label>
          ))}
        </div>
        <p className="superadmin-messaging-fallback">
          With nothing saved here, the channel falls back to{" "}
          <code>MESSAGING_PROVIDER</code> in the environment, currently{" "}
          {PROVIDER_LABELS[envDefault] || envDefault}.
        </p>
        {notice && <p className="superadmin-messaging-saved">{notice}</p>}
      </div>

      <div className="superadmin-table-wrap">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Channel</th>
              <th>Effective</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => {
              const effective = tenant.messagingProvider || platformDefault;
              return (
                <tr key={tenant.id}>
                  <td>
                    {tenant.name}
                    <div className="superadmin-subcell">{tenant.slug}</div>
                  </td>
                  <td>
                    <select
                      value={tenant.messagingProvider || INHERIT}
                      disabled={savingTenantId === tenant.id}
                      onChange={(e) =>
                        handleTenantChange(tenant.id, e.target.value)
                      }
                    >
                      <option value={INHERIT}>
                        Default ({PROVIDER_LABELS[platformDefault]})
                      </option>
                      {providers.map((provider) => (
                        <option key={provider} value={provider}>
                          {PROVIDER_LABELS[provider]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{PROVIDER_LABELS[effective] || effective}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
